<template>
  <div>
    <v-card>
      <v-card-title>
        Training Plans
        <v-spacer />
        <v-btn v-if="selected.length" class="mb-2" color="secondary" @click="isBulkDialogVisible = true">
          MANAGE SELECTED ({{ selected.length }})
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4" offset-md="8">
            <v-text-field
              v-model="searchQuery"
              :append-icon="icons.mdiMagnify"
              label="Search by Name"
              single-line
              hide-details
              dense
              outlined
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="filteredTrainingPlans"
        :loading="isLoading"
        :items-per-page="30"
        :footer-props="{ 'items-per-page-options': [15, 30, 45] }"
        show-select
        item-key="id"
      >
        <template v-slot:item.creatorName="{ item }">
          <div v-if="item.creatorName" class="text-body-2 py-2" style="line-height: 1.4;">
            <div>{{ item.creatorName }}</div>
            <div v-if="item.creatorEmail">
              {{ item.creatorEmail }}
            </div>
          </div>
          <div v-else class="text-caption grey--text font-italic">
            System
          </div>
        </template>

        <template v-slot:item.academy="{ item }">
          <div v-for="academyLine in item.academy.split('<br>')" :key="academyLine" class="text-body-2" style="line-height: 1.4;">
            {{ academyLine }}
          </div>
        </template>

        <template v-slot:item.active="{ item }">
          <v-chip class="table-status-chip" :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? 'Yes' : 'No' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon @click.stop="openDeleteDialog(item)">
            {{ icons.mdiDeleteOutline }}
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="isBulkDialogVisible" max-width="560">
      <v-card>
        <v-card-title>Apply to Selected</v-card-title>
        <v-card-text class="pt-4 pb-2">
          <p>Choose an action for {{ selected.length }} selected item(s).</p>
          <v-radio-group v-model="bulkAction" class="mt-4" hide-details>
            <v-radio label="Assign To Academy / Make Global" value="academy" class="mb-2" />
            <v-radio label="Add To Content Packs" value="content-pack-add" class="mb-2" />
            <v-radio label="Exclude From Content Packs" value="content-pack-delete" class="mb-2" />
            <v-radio label="Mark as Active" value="active-true" class="mb-2" />
            <v-radio label="Mark as Inactive" value="active-false" class="mb-2" />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="isBulkDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="isUpdating" @click="continueBulkAction">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isAcademyDialogVisible" max-width="560">
      <v-card>
        <v-card-title>Assign Training Plans To Academy</v-card-title>
        <v-card-text>
          <v-select v-model="selectedCompanyId" :items="academyOptions" label="Academy" outlined />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeAcademyDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" :disabled="selectedCompanyId === undefined" @click="isAcademyConfirmationVisible = true; isAcademyDialogVisible = false">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isContentPackDialogVisible" max-width="560">
      <v-card>
        <v-card-title>{{ bulkAction === 'content-pack-add' ? 'Add To Content Packs' : 'Exclude From Content Packs' }}</v-card-title>
        <v-card-text>
          <v-select v-model="selectedContentPackId" :items="contentPackOptions" label="Content Pack" outlined />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeContentPackDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" :disabled="!selectedContentPackId" @click="isContentPackConfirmationVisible = true; isContentPackDialogVisible = false">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isAcademyConfirmationVisible" max-width="720">
      <v-card>
        <v-card-title>Assign Training Plans To Academy</v-card-title>
        <v-card-text>
          <p>Are you sure you want to assign these training plans to {{ selectedAcademyName }}?</p>
          <v-list dense class="py-0">
            <v-list-item v-for="item in selected" :key="item.id">
              <v-list-item-content><v-list-item-title>{{ item.title }}</v-list-item-title></v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn text @click="isAcademyConfirmationVisible = false">
            Cancel
          </v-btn><v-btn color="primary" :loading="isUpdating" @click="assignAcademy">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isContentPackConfirmationVisible" max-width="720">
      <v-card>
        <v-card-title>{{ bulkAction === 'content-pack-add' ? 'Add To Content Pack' : 'Exclude From Content Pack' }}</v-card-title>
        <v-card-text>
          <p>{{ contentPackConfirmationMessage }}</p>
          <v-list dense class="py-0">
            <v-list-item v-for="item in selected" :key="item.id">
              <v-list-item-content><v-list-item-title>{{ item.title }}</v-list-item-title></v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn text @click="isContentPackConfirmationVisible = false">
            Cancel
          </v-btn><v-btn color="primary" :loading="isUpdating" @click="updateContentPack">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteDialogVisible" max-width="500">
      <v-card>
        <v-card-title>Delete Training Plan</v-card-title>
        <v-card-text>Are you sure you want to delete "{{ trainingPlanToDelete?.title }}"?</v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn text @click="isDeleteDialogVisible = false">
            Cancel
          </v-btn><v-btn color="error" :loading="isUpdating" @click="deleteSelectedTrainingPlan">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiDeleteOutline, mdiMagnify } from '@mdi/js'
import { computed, onMounted, ref } from 'vue'
import useAcademies from '@/composables/api/academies'
import useContentPacks from '@/composables/api/contentPacks'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useTrainingPlans from '@/composables/api/trainingPlans'
import useUsers from '@/composables/api/users'

export default {
  setup() {
    const {
      trainingPlans,
      isLoading,
      fetchAllTrainingPlans,
      deleteTrainingPlan,
      updateTrainingPlanCompany,
      updateTrainingPlanActive,
    } = useTrainingPlans()
    const { academies, fetchAcademies } = useAcademies()
    const { users, fetchUsers } = useUsers()
    const { contentPacks, fetchContentPacks, updateContentPack: saveContentPack } = useContentPacks()
    const { showSnackbar } = useSnackbar()
    const selected = ref([])
    const searchQuery = ref('')
    const isUpdating = ref(false)
    const isBulkDialogVisible = ref(false)
    const bulkAction = ref('academy')
    const isAcademyDialogVisible = ref(false)
    const isAcademyConfirmationVisible = ref(false)
    const selectedCompanyId = ref(undefined)
    const isContentPackDialogVisible = ref(false)
    const isContentPackConfirmationVisible = ref(false)
    const selectedContentPackId = ref(null)
    const isDeleteDialogVisible = ref(false)
    const trainingPlanToDelete = ref(null)

    const headers = [
      { text: 'NAME', value: 'title', sortable: true },
      { text: 'CREATOR', value: 'creatorName', sortable: true },
      { text: 'ACADEMY', value: 'academy', sortable: true },
      {
        text: 'ACTIVE', value: 'active', sortable: true, width: 100, align: 'center',
      },
      {
        text: 'ACTIONS', value: 'actions', sortable: false, align: 'center', width: 120,
      },
    ]
    const academyOptions = computed(() => [
      { text: 'Make Global', value: null },
      ...academies.value.filter(academy => academy.id !== 'CONTENTPACK').map(academy => ({ text: academy.name, value: academy.id })),
    ])
    const contentPackOptions = computed(() => contentPacks.value.map(pack => ({ text: pack.displayName, value: pack.id })))
    const contentPackNamesByTrainingPlanId = computed(() => {
      const assignments = new Map()

      contentPacks.value.forEach(contentPack => {
        const planIds = contentPack.plans || []
        planIds.forEach(plan => {
          const planId = typeof plan === 'object' ? plan.id : plan
          if (!planId) return

          const contentPackNames = assignments.get(planId) || []
          contentPackNames.push(contentPack.displayName)
          assignments.set(planId, contentPackNames)
        })
      })

      return assignments
    })
    const academyById = computed(() => new Map(academies.value.map(academy => [academy.id, academy.name])))
    const userById = computed(() => new Map(users.value.map(user => [user.id, user])))
    const enrichedTrainingPlans = computed(() => trainingPlans.value.map(plan => {
      const creator = userById.value.get(plan.userId)
      const creatorName = [creator?.firstName, creator?.lastName].filter(Boolean).join(' ')
      const creatorLabel = creatorName || creator?.email || null
      let academy = 'Global'
      if (plan.companyId === 'CONTENTPACK') {
        academy = 'Contentpack'
      } else if (plan.companyId) {
        academy = academyById.value.get(plan.companyId) || plan.companyId
      } else if (contentPackNamesByTrainingPlanId.value.has(plan.id)) {
        academy = 'Global &<br>Contentpack'
      }

      return {
        ...plan,
        active: plan.active !== false,
        academy,
        creatorName: creatorLabel,
        creatorEmail: creatorName ? creator?.email || null : null,
      }
    }))
    const filteredTrainingPlans = computed(() => {
      const search = searchQuery.value.trim().toLowerCase()

      return search ? enrichedTrainingPlans.value.filter(plan => plan.title?.toLowerCase().includes(search)) : enrichedTrainingPlans.value
    })
    const selectedAcademyName = computed(() => (selectedCompanyId.value === null ? 'Global' : academyById.value.get(selectedCompanyId.value) || ''))
    const selectedContentPack = computed(() => contentPacks.value.find(pack => pack.id === selectedContentPackId.value))
    const contentPackConfirmationMessage = computed(() => {
      const action = bulkAction.value === 'content-pack-add' ? 'add' : 'remove'

      return `Are you sure you want to ${action} these training plans ${action === 'add' ? 'to' : 'from'} the content pack "${selectedContentPack.value?.displayName || ''}"?`
    })

    const loadData = async () => {
      await Promise.all([fetchAllTrainingPlans(), fetchAcademies(), fetchUsers(), fetchContentPacks()])
    }
    onMounted(loadData)

    const continueBulkAction = async () => {
      if (bulkAction.value === 'active-true' || bulkAction.value === 'active-false') {
        isUpdating.value = true
        try {
          await updateTrainingPlanActive(selected.value.map(item => item.id), bulkAction.value === 'active-true')
          await fetchAllTrainingPlans()
          selected.value = []
          isBulkDialogVisible.value = false
          showSnackbar('Training plans updated successfully.', SnackbarType.SUCCESS)
        } catch (error) {
          showSnackbar('Failed to update training plans.', SnackbarType.ERROR)
        } finally {
          isUpdating.value = false
        }

        return
      }

      isBulkDialogVisible.value = false
      if (bulkAction.value === 'academy') {
        selectedCompanyId.value = undefined
        isAcademyDialogVisible.value = true

        return
      }

      if (bulkAction.value === 'content-pack-add' && selected.value.some(item => item.companyId && item.companyId !== 'CONTENTPACK')) {
        showSnackbar(
          'Only global training plans can be added to Content Packs. Training plans already assigned to Content Packs can be added to additional Content Packs.',
          SnackbarType.ERROR,
        )

        return
      }

      selectedContentPackId.value = null
      isContentPackDialogVisible.value = true
    }
    const closeAcademyDialog = () => { isAcademyDialogVisible.value = false; selectedCompanyId.value = undefined }
    const closeContentPackDialog = () => { isContentPackDialogVisible.value = false; selectedContentPackId.value = null }
    const assignAcademy = async () => {
      if (selectedCompanyId.value !== null) {
        const contentPackAssignments = selected.value
          .map(plan => ({
            title: plan.title,
            contentPackNames: contentPackNamesByTrainingPlanId.value.get(plan.id) || [],
          }))
          .filter(plan => plan.contentPackNames.length)

        if (contentPackAssignments.length) {
          const planNames = contentPackAssignments.map(plan => plan.title).join(', ')
          const contentPackNames = Array.from(new Set(contentPackAssignments.flatMap(plan => plan.contentPackNames))).join(', ')
          isAcademyConfirmationVisible.value = false
          showSnackbar(
            `Training plans assigned to Content Packs cannot be assigned to an academy. Remove ${planNames} from: ${contentPackNames}.`,
            SnackbarType.ERROR,
          )

          return
        }
      }

      isUpdating.value = true
      try {
        await updateTrainingPlanCompany(selected.value.map(item => item.id), selectedCompanyId.value)
        await fetchAllTrainingPlans()
        selected.value = []
        isAcademyConfirmationVisible.value = false
        showSnackbar('Training plans updated successfully.', SnackbarType.SUCCESS)
      } catch (error) {
        showSnackbar('Failed to update training plans.', SnackbarType.ERROR)
      } finally { isUpdating.value = false }
    }
    const updateContentPack = async () => {
      if (!selectedContentPack.value) return
      isUpdating.value = true
      try {
        const contentPack = structuredClone(selectedContentPack.value)
        const selectedIds = selected.value.map(item => item.id)
        const currentIds = (contentPack.plans || []).map(item => (typeof item === 'object' ? item.id : item))
        const plansToMakeGlobal = bulkAction.value === 'content-pack-delete'
          ? selected.value
            .filter(plan => plan.companyId === 'CONTENTPACK')
            .filter(plan => !contentPacks.value.some(otherContentPack => {
              if (otherContentPack.id === contentPack.id) return false

              return (otherContentPack.plans || []).some(item => (typeof item === 'object' ? item.id : item) === plan.id)
            }))
            .map(plan => plan.id)
          : []
        contentPack.plans = bulkAction.value === 'content-pack-add'
          ? [...new Set([...currentIds, ...selectedIds])]
          : currentIds.filter(id => !selectedIds.includes(id))
        await saveContentPack(contentPack)
        if (bulkAction.value === 'content-pack-add') {
          await updateTrainingPlanCompany(selectedIds, 'CONTENTPACK')
        }
        if (plansToMakeGlobal.length) {
          await updateTrainingPlanCompany(plansToMakeGlobal, null)
        }
        await fetchContentPacks()
        await fetchAllTrainingPlans()
        selected.value = []
        isContentPackConfirmationVisible.value = false
        showSnackbar('Content pack updated successfully.', SnackbarType.SUCCESS)
      } catch (error) {
        showSnackbar('Failed to update content pack.', SnackbarType.ERROR)
      } finally { isUpdating.value = false }
    }
    const openDeleteDialog = item => { trainingPlanToDelete.value = item; isDeleteDialogVisible.value = true }
    const deleteSelectedTrainingPlan = async () => {
      if (!trainingPlanToDelete.value) return
      isUpdating.value = true
      try {
        await deleteTrainingPlan(trainingPlanToDelete.value.id)
        const deletedTrainingPlanId = trainingPlanToDelete.value.id
        await fetchAllTrainingPlans()
        trainingPlans.value = trainingPlans.value.filter(plan => plan.id !== deletedTrainingPlanId)
        isDeleteDialogVisible.value = false
        trainingPlanToDelete.value = null
        showSnackbar('Training plan deleted successfully.', SnackbarType.SUCCESS)
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to delete training plan.'
        showSnackbar(message, SnackbarType.ERROR)
      } finally { isUpdating.value = false }
    }

    return {
      selected,
      searchQuery,
      isLoading,
      isUpdating,
      headers,
      filteredTrainingPlans,
      academyOptions,
      contentPackOptions,
      isBulkDialogVisible,
      bulkAction,
      isAcademyDialogVisible,
      isAcademyConfirmationVisible,
      selectedCompanyId,
      isContentPackDialogVisible,
      isContentPackConfirmationVisible,
      selectedContentPackId,
      selectedAcademyName,
      contentPackConfirmationMessage,
      isDeleteDialogVisible,
      trainingPlanToDelete,
      continueBulkAction,
      closeAcademyDialog,
      closeContentPackDialog,
      assignAcademy,
      updateContentPack,
      openDeleteDialog,
      deleteSelectedTrainingPlan,
      icons: { mdiDeleteOutline, mdiMagnify },
    }
  },
}
</script>
